// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { MaxFundingUnsavedChangesModal } from './MaxFundingUnsavedChangesModal';
import { MaxFundingSaveScenarioModal } from './MaxFundingSaveScenarioModal';

export interface UnsavedChangesController {
  isDirty: boolean;
  setIsDirty: (dirty: boolean) => void;
  registerSaveHandler: (handler: (name: string) => Promise<void>) => void;
  registerDiscardHandler: (handler: () => Promise<void>) => void;
  registerNameProvider: (provider: () => string) => void;
  requestNavigation: (action: () => void) => void;
  openSaveModal: () => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesController | null>(null);

export function UnsavedChangesProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [isDirty, setIsDirty] = useState(false);
  const [unsavedOpen, setUnsavedOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const pendingActionRef = useRef<(() => void) | null>(null);
  const saveHandlerRef = useRef<((name: string) => Promise<void>) | null>(null);
  const discardHandlerRef = useRef<(() => Promise<void>) | null>(null);
  const nameProviderRef = useRef<(() => string) | null>(null);

  const registerSaveHandler = useCallback((handler: (name: string) => Promise<void>) => {
    saveHandlerRef.current = handler;
  }, []);
  const registerDiscardHandler = useCallback((handler: () => Promise<void>) => {
    discardHandlerRef.current = handler;
  }, []);
  const registerNameProvider = useCallback((provider: () => string) => {
    nameProviderRef.current = provider;
  }, []);

  const runPendingAction = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  }, []);

  const requestNavigation = useCallback(
    (action: () => void) => {
      if (!isDirty) {
        action();
        return;
      }
      pendingActionRef.current = action;
      setSaveError(null);
      setUnsavedOpen(true);
    },
    [isDirty],
  );

  const openSaveModal = useCallback(() => {
    setSaveError(null);
    setSaveOpen(true);
  }, []);

  const handleDiscard = useCallback(async () => {
    if (!discardHandlerRef.current) {
      // No discard handler registered — just clear and run the pending action.
      setIsDirty(false);
      setUnsavedOpen(false);
      runPendingAction();
      return;
    }
    try {
      setBusy(true);
      await discardHandlerRef.current();
      setIsDirty(false);
      setUnsavedOpen(false);
      runPendingAction();
    } catch (err) {
      // Surface as inline error on the modal — leave it open so the user can retry.
      const message = (err as Error)?.message ?? 'Discard failed';
      setSaveError(message);
    } finally {
      setBusy(false);
    }
  }, [runPendingAction]);

  const handleUnsavedSave = useCallback(() => {
    // Transition from unsaved modal to save modal.
    setUnsavedOpen(false);
    setSaveError(null);
    setSaveOpen(true);
  }, []);

  const handleSaveConfirm = useCallback(
    async (name: string) => {
      if (!saveHandlerRef.current) return;
      try {
        setBusy(true);
        setSaveError(null);
        await saveHandlerRef.current(name);
        setIsDirty(false);
        setSaveOpen(false);
        runPendingAction();
      } catch (err) {
        const message = (err as Error)?.message ?? 'Save failed';
        setSaveError(message);
        // Drop the pending action — the user explicitly tried to save and it failed,
        // so we shouldn't silently navigate away.
        pendingActionRef.current = null;
      } finally {
        setBusy(false);
      }
    },
    [runPendingAction],
  );

  const handleUnsavedClose = useCallback(() => {
    if (busy) return;
    pendingActionRef.current = null;
    setUnsavedOpen(false);
  }, [busy]);

  const handleSaveClose = useCallback(() => {
    if (busy) return;
    pendingActionRef.current = null;
    setSaveOpen(false);
  }, [busy]);

  const controller = useMemo<UnsavedChangesController>(
    () => ({
      isDirty,
      setIsDirty,
      registerSaveHandler,
      registerDiscardHandler,
      registerNameProvider,
      requestNavigation,
      openSaveModal,
    }),
    [
      isDirty,
      setIsDirty,
      registerSaveHandler,
      registerDiscardHandler,
      registerNameProvider,
      requestNavigation,
      openSaveModal,
    ],
  );

  return (
    <UnsavedChangesContext.Provider value={controller}>
      {children}
      <MaxFundingUnsavedChangesModal
        open={unsavedOpen}
        busy={busy}
        onClose={handleUnsavedClose}
        onDiscard={() => void handleDiscard()}
        onSave={handleUnsavedSave}
      />
      <MaxFundingSaveScenarioModal
        open={saveOpen}
        defaultName={nameProviderRef.current?.() ?? ''}
        saving={busy}
        error={saveError}
        onClose={handleSaveClose}
        onSave={(name) => void handleSaveConfirm(name)}
      />
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChanges(): UnsavedChangesController {
  const ctx = useContext(UnsavedChangesContext);
  if (!ctx) {
    throw new Error('useUnsavedChanges must be used inside an UnsavedChangesProvider');
  }
  return ctx;
}

export function useUnsavedChangesOptional(): UnsavedChangesController | null {
  return useContext(UnsavedChangesContext);
}
